import { memo, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import dayjs from 'dayjs';
import {
  useApiContext,
  useCallbackCheckForBonusToActivate,
  useGetFullInfo,
  useUpdateInfo
} from 'react-easyrocket';
import { Button } from 'src/components/Buttons/Button';
import {
  useForm,
  FieldType,
  validateMaxLength,
  validateRequired,
  validateDateOfBirthSingleField
} from 'src/hooks/useForm';
import { Input } from 'src/components/Inputs/Input';
import { RadioGroup } from 'src/components/RadioButtons/RadioGroup';
import { useModalContext } from 'src/hooks/useModalContext';
import { useRoute } from 'src/router/hooks';
import { nameRegex } from 'src/constants/regex';
import { CustomCalendar } from 'src/components/CustomCalendar';

interface StepTwoProps {
  changeStep: (value: number) => void;
  disableFields?: boolean;
}

const _StepTwo = ({ changeStep, disableFields = false }: StepTwoProps) => {
  const { isAuthenticated } = useApiContext();
  const { t } = useTranslation();
  const { closeModal } = useModalContext();
  const activateBonus = useCallbackCheckForBonusToActivate();
  const { ROUTE } = useRoute();

  const handleChangeStep = useCallback(() => {
    changeStep(2);
  }, [changeStep]);
  const { mutateAsync, isLoading } = useUpdateInfo({ onSuccess: handleChangeStep });
  const {
    values,
    errors,
    setValues,
    fieldsProps: { nameProps, surnameProps, birthdayProps, genderProps },
    touched,
    isFormValid
  } = useForm<{
    name: FieldType.Text;
    surname: FieldType.Text;
    birthday: FieldType.Text;
    gender: FieldType.Radio;
  }>({
    name: {
      placeholder: `${t('general.firstName')}`,
      fieldType: FieldType.Text,
      validation: [validateMaxLength(256)]
    },
    surname: {
      placeholder: `${t('general.lastName')}`,
      fieldType: FieldType.Text,
      validation: [validateMaxLength(256)]
    },
    birthday: {
      fieldType: FieldType.Text,
      validation: [
        validateRequired,
        (values: string) => validateDateOfBirthSingleField(values, 'YYYY-MM-DD')
      ]
    },
    gender: {
      fieldType: FieldType.Radio,
      options: [
        { id: 'm', name: `${t('accountProfileMain.male')}` },
        { id: 'f', name: `${t('accountProfileMain.female')}` }
      ]
    }
  });

  const handleClick = useCallback(() => {
    mutateAsync({
      name: values.name ? values.name.replace(/' '/g, '') : undefined,
      surname: values.surname ? values.surname.replace(/' '/g, '') : undefined,
      sex: values.gender ? values.gender : undefined,
      birthday: values?.birthday
    });
  }, [mutateAsync, values?.birthday, values.gender, values.name, values.surname]);

  const { fullInfo } = useGetFullInfo({
    onSuccess: (response) => {
      setValues({
        name: response.data.name,
        surname: response.data.surname,
        gender: response.data.sex,
        birthday: response.data.birthday
          ? dayjs(response.data.birthday).format('YYYY-MM-DD').toString()
          : ''
      });
    }
  });

  return (
    <form
      className="flex flex-col w-full justify-between"
      onSubmit={(e) => e.preventDefault()}
      autoComplete="off">
      <div className="flex flex-col gap-2 w-full items-center">
        <Input
          {...nameProps}
          regexAllowedSymbols={nameRegex}
          autoComplete="off"
          disabled={disableFields}
        />
        {touched.name && errors.name && (
          <span className="text-i_text_error text-xs">{errors.name}</span>
        )}
        <Input
          {...surnameProps}
          regexAllowedSymbols={nameRegex}
          autoComplete="off"
          disabled={disableFields}
        />
        {touched.surname && errors.surname && (
          <span className="text-i_text_error text-xs">{errors.surname}</span>
        )}
        <div className="flex flex-col w-full">
          <div className="flex w-full flex-col col-span-3">
            <CustomCalendar
              labelClassName="text-base text-i_label !ml-2"
              {...birthdayProps}
              isMonthsShort
              classNameDropdown="max-w-full"
              classNameContainer="gap-y-0 gap-x-[15px]"
              disabled={Boolean(fullInfo?.birthday)}
              label={`${t('accountProfileMain.birthDate')}`}
              placeholderDay={`${t('general.dd')}`}
              placeholderMonth={`${t('general.mm')}`}
              placeholderYear={`${t('general.yyyy')}`}
            />
          </div>
          {errors.birthday && (
            <span className="text-xs text-i_text_error mt-2">{errors.birthday}</span>
          )}
        </div>
        <div className="z-0 w-full ml-[15px]">
          <RadioGroup {...genderProps} checkValue={values?.gender} />
        </div>
      </div>
      <div className="flex flex-col items-center gap-4">
        <Button
          title={`${t('registration.nextStep')}`}
          className="max-w-full"
          loading={isLoading}
          mode="button-primary"
          disabled={!isFormValid}
          onClick={() => (isAuthenticated ? handleClick() : changeStep(2))}>
          {t('registration.nextStep')}
        </Button>
        <p className="w-full font-medium text-xs m-auto text-sum_text justify-center text-center 768:mb-2.5">
          <Trans
            i18nKey="registration.youCanAlsoFillInfo"
            components={{
              2: (
                <Link
                  to={ROUTE.ACCOUNT_PROFILE_MAIN.get()}
                  onClick={() => {
                    closeModal();
                    activateBonus();
                  }}
                  className="cursor-pointer text-sum_login underline pl-1"
                />
              )
            }}
          />
        </p>
      </div>
    </form>
  );
};

export const StepTwo = memo(_StepTwo);
